
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';

function MapaSucursales2({
  provincia,
  localidad,
  metodoEntrega,          // "sucursal" | "domicilio"
  sucursales = [],
  onSelect,
  // NUEVO:
  onResults,            // devuelve el array de Google Places al padre
  focusPlace,           // desde la lista, centramos/abrimos/bounce
}) {
  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [marcadoresCorreo, setMarcadoresCorreo] = useState([]);
  const [marcadoresManuales, setMarcadoresManuales] = useState([]);
  const marcadoresPorId = useRef({}); // place_id -> marker

  // Helpers
  const clearCorreoMarkers = () => {
    marcadoresCorreo.forEach((m) => m.setMap(null));
    setMarcadoresCorreo([]);
    marcadoresPorId.current = {};
  };
  const clearManualMarkers = () => {
    marcadoresManuales.forEach((m) => m.setMap(null));
    setMarcadoresManuales([]);
  };

  // Inicializar/Centrar por provincia
  useEffect(() => {
    if (!window.google || !provincia) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${provincia}, Argentina` }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const centro = { lat: loc.lat(), lng: loc.lng() };

        if (!mapInstance) {
          const map = new window.google.maps.Map(mapRef.current, {
            center: centro,
            zoom: 10,
          });
          setMapInstance(map);
          setInfoWindow(new window.google.maps.InfoWindow());
        } else {
          mapInstance.setCenter(centro);
          mapInstance.setZoom(10);
        }
      }
    });
  }, [provincia]); // eslint-disable-line react-hooks/exhaustive-deps

  // Centrar en localidad
  useEffect(() => {
    if (!window.google || !localidad || !provincia || !mapInstance) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${localidad}, ${provincia}, Argentina` }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const punto = { lat: loc.lat(), lng: loc.lng() };
        mapInstance.setCenter(punto);
        mapInstance.setZoom(14);

        // Si estamos en "sucursal", disparar búsqueda
        if (metodoEntrega === "sucursal") {
          buscarCorreoArgentino(punto);
        }
      } else {
        onResults && onResults([]);
      }
    });
  }, [localidad]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar de modo: limpiar pines si salimos de "sucursal"
  useEffect(() => {
    if (!mapInstance) return;
    if (metodoEntrega !== "sucursal") {
      clearCorreoMarkers();
      clearManualMarkers();
      infoWindow?.close();
    } else {
      const c = mapInstance.getCenter?.();
      if (c) {
        buscarCorreoArgentino({ lat: c.lat(), lng: c.lng() });
      }
    }
  }, [metodoEntrega]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pintar sucursales manuales (si pasás alguna)
  useEffect(() => {
    if (!mapInstance || !sucursales?.length) return;

    // limpiar anteriores manuales
    clearManualMarkers();

    const nuevos = sucursales.map((s) => {
      const marker = new window.google.maps.Marker({
        map: mapInstance,
        position: { lat: s.lat, lng: s.lng },
        title: s.nombre,
        icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
      });

      marker.addListener("click", () => {
        if (!infoWindow) return;
        abrirInfoDeSucursal(marker, {
          nombre: s.nombre,
          address: s.address || "",
          lat: s.lat,
          lng: s.lng,
          placeId: s.placeId || null,
        });
      });

      return marker;
    });

    setMarcadoresManuales(nuevos);

    return () => {
      nuevos.forEach((m) => m.setMap(null));
    };
  }, [sucursales, mapInstance, infoWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buscar sucursales (Google Places)
  function buscarCorreoArgentino(location) {
    if (!window.google || !mapInstance || metodoEntrega !== "sucursal") return;

    // limpiar previos
    clearCorreoMarkers();

    const service = new window.google.maps.places.PlacesService(mapInstance);
    service.textSearch(
      { query: "Correo Argentino sucursal", location },
      (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results?.length > 0
        ) {
          const nuevos = results.map((place) => {
            const marker = new window.google.maps.Marker({
              map: mapInstance,
              position: place.geometry.location,
              title: place.name,
              icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
            });

            // NUEVO: guardar referencia por place_id
            marcadoresPorId.current[place.place_id] = marker;

            marker.addListener("click", () => {
              if (!infoWindow) return;
              abrirInfoDeSucursal(marker, {
                nombre: place.name,
                address: place.formatted_address || place.vicinity || "",
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                placeId: place.place_id,
              });
            });

            return marker;
          });
          setMarcadoresCorreo(nuevos);
          onResults && onResults(results); // NUEVO: devuelvo lista al padre
        } else {
          onResults && onResults([]);
        }
      }
    );
  }

  // Centrar y bounce al seleccionar desde la lista (focusPlace)
  useEffect(() => {
    if (!focusPlace || !mapInstance) return;
    const marker = marcadoresPorId.current[focusPlace.place_id];
    if (!marker) return;

    mapInstance.panTo(marker.getPosition());
    mapInstance.setZoom(15);

    if (infoWindow) {
      abrirInfoDeSucursal(marker, {
        nombre: focusPlace.name,
        address: focusPlace.formatted_address || focusPlace.vicinity || "",
        lat: marker.getPosition().lat(),
        lng: marker.getPosition().lng(),
        placeId: focusPlace.place_id,
      });
    }

    marker.setAnimation(window.google.maps.Animation.BOUNCE);
    setTimeout(() => marker.setAnimation(null), 1200);
  }, [focusPlace, mapInstance, infoWindow]);

  // InfoWindow con botón "Usar esta sucursal"
  function abrirInfoDeSucursal(marker, data) {
    if (!infoWindow) return;
    const content = `
      <div style="max-width:240px">
        <div style="font-weight:600; margin-bottom:4px;">${escapeHtml(data.nombre)}</div>
        <div style="font-size:12px; color:#555; margin-bottom:8px;">${escapeHtml(data.address || "Dirección no disponible")}</div>
        <button id="btnSeleccionarSucursal" style="
          background:#166534; color:white; border:none; border-radius:8px;
          padding:8px 10px; cursor:pointer; width:100%;
        ">Usar esta sucursal</button>
      </div>
    `;
    infoWindow.setContent(content);
    infoWindow.open({ map: mapInstance, anchor: marker });

    window.google.maps.event.addListenerOnce(infoWindow, "domready", () => {
      const btn = document.getElementById("btnSeleccionarSucursal");
      if (btn) {
        btn.onclick = () => {
          onSelect &&
            onSelect({
              nombre: data.nombre,
              lat: data.lat,
              lng: data.lng,
              address: data.address,
              placeId: data.placeId || null,
            });
          infoWindow.close();
        };
      }
    });
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  // Limpieza total al desmontar
  useEffect(() => {
    return () => {
      clearCorreoMarkers();
      clearManualMarkers();
      infoWindow?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IMPORTANTE: que el contenedor se adapte al alto/width del padre
  return (
    <div ref={mapRef} className="w-full h-full" />
  );
}

export default function MapaSucursales({
  provincia,
  localidad,
  metodoEntrega,          // "sucursal" | "domicilio"
  sucursales = [],
  onSelect,
  onResults,              // devuelve el array de Google Places al padre
  focusPlace,             // desde la lista, centramos/abrimos/bounce
}) {
  const mapRef = useRef(null);

  // Instancias en refs para evitar reinits y problemas con StrictMode
  const mapInstanceRef = useRef(null);
  const infoWindowRef  = useRef(null);

  const marcadoresCorreoRef   = useRef([]);  // markers de búsqueda (Correo Argentino)
  const marcadoresManualesRef = useRef([]);  // markers manuales (prop)
  const marcadoresPorId       = useRef({});  // place_id -> marker

  // Helpers: limpiar marcadores
  const clearCorreoMarkers = () => {
    marcadoresCorreoRef.current.forEach((m) => m.setMap(null));
    marcadoresCorreoRef.current = [];
    marcadoresPorId.current = {};
  };
  const clearManualMarkers = () => {
    marcadoresManualesRef.current.forEach((m) => m.setMap(null));
    marcadoresManualesRef.current = [];
  };

  // Crear mapa una sola vez cuando haya contenedor
  const ensureMapInitialized = (center) => {
    if (!window.google) return false;
    if (!mapRef.current) return false;
    if (mapInstanceRef.current) return true; // ya creado

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: center || { lat: -34.6037, lng: -58.3816 }, // fallback CABA
      zoom: center ? 10 : 5,
    });
    infoWindowRef.current = new window.google.maps.InfoWindow();
    return true;
  };

  // === RECENTRAR SOLO CUANDO HAY PROVINCIA + LOCALIDAD ===
  useEffect(() => {
    // Si no hay provincia o localidad, no mostramos ni inicializamos nada
    if (!window.google || !provincia || !localidad) {
      onResults && onResults([]); // vaciar lista si la había
      return;
    }

    let cancelled = false;
    if (!mapRef.current) return;

    // Geocoding de "localidad, provincia" (único que define centro)
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${localidad}, ${provincia}, Argentina` }, (results, status) => {
      if (cancelled) return;
      if (status === "OK" && results?.[0]) {
        const loc = results[0].geometry.location;
        const punto = { lat: loc.lat(), lng: loc.lng() };

        // Aseguramos que el <div> existe en el DOM al momento de crear/centrar
        requestAnimationFrame(() => {
          if (cancelled || !mapRef.current) return;
          ensureMapInitialized(punto);
          const map = mapInstanceRef.current;
          if (!map) return;

          map.setCenter(punto);
          map.setZoom(14);

          if (metodoEntrega === "sucursal") {
            buscarCorreoArgentino(punto);
          } else {
            clearCorreoMarkers();
            onResults && onResults([]);
          }
        });
      } else {
        // No encontró la localidad → vaciar resultados
        clearCorreoMarkers();
        onResults && onResults([]);
      }
    });

    return () => { cancelled = true; };
  }, [provincia, localidad, metodoEntrega]);

  // Cambiar modo: si salimos de "sucursal", limpiamos
  useEffect(() => {
    if (metodoEntrega !== "sucursal") {
      clearCorreoMarkers();
      infoWindowRef.current?.close();
    }
  }, [metodoEntrega]);

  // Pintar sucursales manuales (prop)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !sucursales?.length) return;

    clearManualMarkers();

    const nuevos = sucursales.map((s) => {
      const marker = new window.google.maps.Marker({
        map,
        position: { lat: s.lat, lng: s.lng },
        title: s.nombre,
        icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
      });

      marker.addListener("click", () => {
        abrirInfoDeSucursal(marker, {
          nombre: s.nombre,
          address: s.address || "",
          lat: s.lat,
          lng: s.lng,
          placeId: s.placeId || null,
        });
      });

      return marker;
    });

    marcadoresManualesRef.current = nuevos;

    return () => {
      nuevos.forEach((m) => m.setMap(null));
    };
  }, [sucursales]);

  // Buscar sucursales (Google Places) cerca de 'location'
  function buscarCorreoArgentino(location) {
    const map = mapInstanceRef.current;
    if (!window.google || !map || metodoEntrega !== "sucursal") return;

    clearCorreoMarkers();

    const service = new window.google.maps.places.PlacesService(map);
    service.textSearch(
      { query: "Correo Argentino sucursal", location },
      (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results?.length > 0
        ) {
          const nuevos = results.map((place) => {
            const marker = new window.google.maps.Marker({
              map,
              position: place.geometry.location,
              title: place.name,
              icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
            });

            marcadoresPorId.current[place.place_id] = marker;

            marker.addListener("click", () => {
              abrirInfoDeSucursal(marker, {
                nombre: place.name,
                address: place.formatted_address || place.vicinity || "",
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                placeId: place.place_id,
              });
            });

            return marker;
          });

          marcadoresCorreoRef.current = nuevos;
          onResults && onResults(results);
        } else {
          onResults && onResults([]);
        }
      }
    );
  }

  // Centrar y abrir InfoWindow cuando seleccionan desde la lista
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!focusPlace || !map) return;

    const marker = marcadoresPorId.current[focusPlace.place_id];
    if (!marker) return;

    map.panTo(marker.getPosition());
    map.setZoom(15);

    abrirInfoDeSucursal(marker, {
      nombre: focusPlace.name,
      address: focusPlace.formatted_address || focusPlace.vicinity || "",
      lat: marker.getPosition().lat(),
      lng: marker.getPosition().lng(),
      placeId: focusPlace.place_id,
    });

    marker.setAnimation(window.google.maps.Animation.BOUNCE);
    setTimeout(() => marker.setAnimation(null), 1200);
  }, [focusPlace]);

  // InfoWindow con botón "Usar esta sucursal"
  function abrirInfoDeSucursal(marker, data) {
    const map = mapInstanceRef.current;
    const iw  = infoWindowRef.current;
    if (!map || !iw) return;

    const content = `
      <div style="max-width:240px">
        <div style="font-weight:600; margin-bottom:4px;">${escapeHtml(data.nombre)}</div>
        <div style="font-size:12px; color:#555; margin-bottom:8px;">${escapeHtml(data.address || "Dirección no disponible")}</div>
        <button id="btnSeleccionarSucursal" style="
          background:#166534; color:white; border:none; border-radius:8px;
          padding:8px 10px; cursor:pointer; width:100%;
        ">Usar esta sucursal</button>
      </div>
    `;
    iw.setContent(content);
    iw.open({ map, anchor: marker });

    window.google.maps.event.addListenerOnce(iw, "domready", () => {
      const btn = document.getElementById("btnSeleccionarSucursal");
      if (btn) {
        btn.onclick = () => {
          onSelect &&
            onSelect({
              nombre: data.nombre,
              lat: data.lat,
              lng: data.lng,
              address: data.address,
              placeId: data.placeId || null,
            });
          iw.close();
        };
      }
    });
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      clearCorreoMarkers();
      clearManualMarkers();
      infoWindowRef.current?.close();
      mapInstanceRef.current = null;
      infoWindowRef.current  = null;
    };
  }, []);

  // Contenedor del mapa (debe tener alto via clases del padre)
  return <div ref={mapRef} className="w-full h-full" />;
}



